const SKELETON_CONFIG = {
  childNodesPerParent: 8,
  horizontalGap: 240,
  verticalGap: 100,
  leftPadding: 100,
  topPadding: 50,
  grandchildNodesPerParent: 3
};

export const generateSkeletonTree = (labels = {}) => {
  const { customerLabel = 'ERP System', clientLabel = 'eCommerce System' } = labels;
  
  const nodes = [];
  const edges = [];

  const skeletonNodes = [
    {
      id: 'skeleton-customer',
      type: 'skeleton',
      data: {
        label: customerLabel,
        isRootNode: true,
        isSource: true,
        isParent: true,
        isSkeleton: true
      },
      position: { x: SKELETON_CONFIG.leftPadding - 1000, y: SKELETON_CONFIG.topPadding + 100 }
    },
    {
      id: 'skeleton-client',
      type: 'skeleton',
      data: {
        label: clientLabel,
        isRootNode: true,
        isSource: false,
        isParent: true,
        isSkeleton: true
      },
      position: { x: SKELETON_CONFIG.leftPadding + 520, y: SKELETON_CONFIG.topPadding + 100 }
    }
  ];

  const customerY = SKELETON_CONFIG.topPadding + 100;
  const totalHeight = SKELETON_CONFIG.childNodesPerParent * SKELETON_CONFIG.verticalGap;
  let customerChildY = customerY - totalHeight / 2;
  if (customerChildY < SKELETON_CONFIG.topPadding) customerChildY = SKELETON_CONFIG.topPadding;

  const parentNodesWithChildren = new Set();

  for (let i = 0; i < SKELETON_CONFIG.childNodesPerParent; i++) {
    const nodeId = `skeleton-customer-${i}`;
    const x = SKELETON_CONFIG.leftPadding - 1000 + SKELETON_CONFIG.horizontalGap;
    const y = customerChildY + i * SKELETON_CONFIG.verticalGap;

    const hasChildren = i === SKELETON_CONFIG.childNodesPerParent - 2;
    if (hasChildren) parentNodesWithChildren.add(nodeId);

    nodes.push({
      id: nodeId,
      type: 'skeleton',
      data: {
        parentId: 'skeleton-customer',
        isRootNode: false,
        isSource: true,
        isParent: hasChildren,
        isSkeleton: true
      },
      position: { x, y }
    });

    edges.push({
      id: `skeleton-edge-customer-${i}`,
      source: 'skeleton-customer',
      target: nodeId,
      type: 'tree',
      animated: false,
      style: {
        strokeWidth: 2,
        opacity: 0.6
      }
    });

    if (hasChildren) {
      const grandchildY = y - (SKELETON_CONFIG.grandchildNodesPerParent * SKELETON_CONFIG.verticalGap) / 2;
      for (let j = 0; j < SKELETON_CONFIG.grandchildNodesPerParent; j++) {
        const grandchildId = `skeleton-customer-${i}-child-${j}`;
        const grandchildX = x + SKELETON_CONFIG.horizontalGap;
        const grandchildYPos = grandchildY + j * SKELETON_CONFIG.verticalGap;

        nodes.push({
          id: grandchildId,
          type: 'skeleton',
          data: {
            parentId: nodeId,
            isRootNode: false,
            isSource: true,
            isParent: false,
            isSkeleton: true
          },
          position: { x: grandchildX, y: grandchildYPos }
        });

        edges.push({
          id: `skeleton-edge-customer-${i}-${j}`,
          source: nodeId,
          target: grandchildId,
          type: 'tree',
          animated: false,
          style: {
            strokeWidth: 2,
            opacity: 0.6
          }
        });
      }
    }
  }

  let clientChildY = customerY - totalHeight / 2;
  if (clientChildY < SKELETON_CONFIG.topPadding) clientChildY = SKELETON_CONFIG.topPadding;

  for (let i = 0; i < SKELETON_CONFIG.childNodesPerParent; i++) {
    const nodeId = `skeleton-client-${i}`;
    const x = SKELETON_CONFIG.leftPadding + 520 - SKELETON_CONFIG.horizontalGap;
    const y = clientChildY + i * SKELETON_CONFIG.verticalGap;

    const hasChildren = i === SKELETON_CONFIG.childNodesPerParent - 3;
    if (hasChildren) parentNodesWithChildren.add(nodeId);

    nodes.push({
      id: nodeId,
      type: 'skeleton',
      data: {
        parentId: 'skeleton-client',
        isRootNode: false,
        isSource: false,
        isParent: hasChildren,
        isSkeleton: true
      },
      position: { x, y }
    });

    edges.push({
      id: `skeleton-edge-client-${i}`,
      source: 'skeleton-client',
      target: nodeId,
      type: 'tree',
      animated: false,
      style: {
        strokeWidth: 2,
        opacity: 0.6
      }
    });

    if (hasChildren) {
      const grandchildY = y - (SKELETON_CONFIG.grandchildNodesPerParent * SKELETON_CONFIG.verticalGap) / 2;
      for (let j = 0; j < SKELETON_CONFIG.grandchildNodesPerParent; j++) {
        const grandchildId = `skeleton-client-${i}-child-${j}`;
        const grandchildX = x - SKELETON_CONFIG.horizontalGap;
        const grandchildYPos = grandchildY + j * SKELETON_CONFIG.verticalGap;

        nodes.push({
          id: grandchildId,
          type: 'skeleton',
          data: {
            parentId: nodeId,
            isRootNode: false,
            isSource: false,
            isParent: false,
            isSkeleton: true
          },
          position: { x: grandchildX, y: grandchildYPos }
        });

        edges.push({
          id: `skeleton-edge-client-${i}-${j}`,
          source: nodeId,
          target: grandchildId,
          type: 'tree',
          animated: false,
          style: {
            strokeWidth: 2,
            opacity: 0.6
          }
        });
      }
    }
  }

  const mappingPairs = [
    { source: 'skeleton-customer-0', target: 'skeleton-client-0' },
    { source: 'skeleton-customer-1', target: 'skeleton-client-1' },
    { source: 'skeleton-customer-2', target: 'skeleton-client-2' },
    { source: 'skeleton-customer-3', target: 'skeleton-client-3' },
    { source: 'skeleton-customer-4', target: 'skeleton-client-4' },
    { source: 'skeleton-customer-6', target: 'skeleton-client-6' },
  ];

  mappingPairs.forEach((pair, index) => {
    edges.push({
      id: `skeleton-field-mapping-${index}`,
      source: pair.source,
      target: pair.target,
      type: 'custom',
      animated: false,
      style: {
        stroke: '#c9d9f8',
        strokeWidth: 1.5,
        strokeDasharray: '5,5',
        opacity: 0.5
      },
      data: {
        label: '',
        transformationType: 'none',
        hasTransformation: false
      }
    });
  });

  return {
    nodes: [...skeletonNodes, ...nodes],
    edges
  };
};
